/* ==========================================================================
   oh-tf.js — Optimal Human · Greens Gummies FREE TRIAL lander

   Adds ONLY the primitives assets/oh-engine.js has no equivalent for. Reveals,
   parallax, count-ups and marquees all still come from the engine
   ([data-reveal], [data-parallax], [data-countup], [data-oh-drift]) — this file
   never re-implements them.

     [data-tf-mag]              cursor-follow pull on a CTA (writes --mx/--my)
     [data-tf-pin]              sticky-driven horizontal scroll sequence
     [data-tf-scrub]            scroll-progress → CSS vars on an element
     [data-tf-collapse]         accessible disclosure (the offer explainer)
     [data-tf-atc]              clear cart → add trial → /checkout
     [data-tf-stickybar]        reveal past the hero, yield to the offer section

   Editor-safe: OHTF.scan(root) (re)binds everything, every bind is guarded by a
   data-tf-bound-* attribute, and the file rescans on shopify:section:load.
   Everything is transform/opacity and every effect checks prefers-reduced-motion
   before it binds. One shared rAF ticker drives pin + scrub + stickybar.
   ========================================================================== */
(function () {
  'use strict';

  if (window.OHTF && window.OHTF.__loaded) return;
  var OHTF = window.OHTF = window.OHTF || {};
  OHTF.__loaded = true;

  var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var mqDesktop = function () { return window.matchMedia('(min-width: 901px)').matches; };

  function collect(scope, sel) {
    var list = [].slice.call(scope.querySelectorAll(sel));
    if (scope.nodeType === 1 && scope.matches && scope.matches(sel)) list.unshift(scope);
    return list;
  }
  function once(el, key) {
    var a = 'data-tf-bound-' + key;
    if (el.hasAttribute(a)) return false;
    el.setAttribute(a, '');
    return true;
  }
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };

  /* ------------------------------------------------------------------ *
   * Shared scroll ticker — one rAF for every scroll-driven consumer.
   * ------------------------------------------------------------------ */
  var consumers = [], ticking = false, bound = false;
  function tick() {
    ticking = false;
    for (var i = consumers.length - 1; i >= 0; i--) {
      if (!consumers[i].el.isConnected) { consumers.splice(i, 1); continue; }
      try { consumers[i].run(); } catch (_) { /* one bad consumer must not kill the loop */ }
    }
  }
  function request() { if (!ticking) { ticking = true; requestAnimationFrame(tick); } }
  function addConsumer(el, run) {
    consumers.push({ el: el, run: run });
    if (!bound) {
      bound = true;
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
      window.addEventListener('load', request);
    }
    run();
  }

  /* ------------------------------------------------------------------ *
   * 1) MAGNETIC CTA
   * ------------------------------------------------------------------ */
  function bindMag(scope) {
    if (reduced) return;
    collect(scope, '[data-tf-mag]').forEach(function (el) {
      if (!once(el, 'mag')) return;
      var strength = parseFloat(el.getAttribute('data-tf-mag')) || 7;
      el.addEventListener('pointermove', function (e) {
        if (e.pointerType === 'touch' || !mqDesktop()) return;
        var r = el.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
        var dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
        el.style.setProperty('--mx', (clamp(dx, -1, 1) * strength).toFixed(2) + 'px');
        el.style.setProperty('--my', (clamp(dy, -1, 1) * strength * .6).toFixed(2) + 'px');
      });
      var reset = function () { el.style.setProperty('--mx', '0px'); el.style.setProperty('--my', '0px'); };
      el.addEventListener('pointerleave', reset);
      el.addEventListener('blur', reset);
    });
  }

  /* ------------------------------------------------------------------ *
   * 2) PINNED HORIZONTAL SEQUENCE
   *
   * Pure sticky — no scroll hijacking, so native scrolling, keyboard paging and
   * the browser's own momentum all keep working. Markup contract:
   *
   *   <div data-tf-pin>                 tall spacer, height set here
   *     <div data-tf-pin-stage>         position:sticky; top:0
   *       <div data-tf-pin-track>       translateX driven by progress
   *         <div data-tf-pin-panel>…    one per step
   *
   * Below 901px, or under reduced-motion, nothing binds and the CSS fallback
   * renders the panels as a normal vertical stack.
   * ------------------------------------------------------------------ */
  function bindPin(scope) {
    collect(scope, '[data-tf-pin]').forEach(function (root) {
      if (!once(root, 'pin')) return;
      var stage = root.querySelector('[data-tf-pin-stage]');
      var track = root.querySelector('[data-tf-pin-track]');
      if (!stage || !track) return;

      function active() { return !reduced && mqDesktop(); }

      function layout() {
        if (!active()) {
          root.style.height = '';
          track.style.transform = '';
          root.classList.remove('is-pinned');
          return 0;
        }
        root.classList.add('is-pinned');
        /* distance the track must travel, plus a full stage of lead-in/out */
        var travel = Math.max(0, track.scrollWidth - stage.clientWidth);
        root.style.height = (stage.clientHeight + travel) + 'px';
        return travel;
      }

      var travel = layout();

      addConsumer(root, function () {
        if (!active()) return;
        var r = root.getBoundingClientRect();
        var span = root.offsetHeight - stage.clientHeight;
        if (span <= 0) return;
        var p = clamp(-r.top / span, 0, 1);
        track.style.transform = 'translate3d(' + (-p * travel).toFixed(1) + 'px,0,0)';
        root.style.setProperty('--p', p.toFixed(4));
      });

      var ro = window.ResizeObserver ? new ResizeObserver(function () { travel = layout(); request(); }) : null;
      if (ro) { ro.observe(track); ro.observe(stage); }
      window.addEventListener('resize', function () { travel = layout(); request(); }, { passive: true });
    });
  }

  /* ------------------------------------------------------------------ *
   * 3) SCROLL SCRUB — writes --p (0→1) and --deg while the element crosses
   *    the viewport, so any transform can be expressed in CSS.
   * ------------------------------------------------------------------ */
  function bindScrub(scope) {
    if (reduced) return;
    collect(scope, '[data-tf-scrub]').forEach(function (el) {
      if (!once(el, 'scrub')) return;
      var deg = parseFloat(el.getAttribute('data-tf-scrub-deg'));
      if (isNaN(deg)) deg = 18;
      addConsumer(el, function () {
        var r = el.getBoundingClientRect();
        var vh = window.innerHeight || 1;
        /* 0 as the element enters from the bottom, 1 as it leaves at the top */
        var p = clamp((vh - r.top) / (vh + r.height), 0, 1);
        el.style.setProperty('--p', p.toFixed(4));
        el.style.setProperty('--deg', ((p - .5) * 2 * deg).toFixed(2) + 'deg');
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 4) COLLAPSE — the offer explainer. Animates max-height off the measured
   *    scrollHeight, then releases it so reflow can never clip the content.
   * ------------------------------------------------------------------ */
  function bindCollapse(scope) {
    collect(scope, '[data-tf-collapse]').forEach(function (btn) {
      if (!once(btn, 'collapse')) return;
      var panel = document.getElementById(btn.getAttribute('aria-controls') || '');
      if (!panel) return;
      var open = btn.getAttribute('aria-expanded') === 'true';
      panel.style.overflow = 'hidden';
      panel.style.maxHeight = open ? 'none' : '0px';
      panel.hidden = false;

      btn.addEventListener('click', function () {
        open = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (reduced) { panel.style.maxHeight = open ? 'none' : '0px'; return; }
        if (open) {
          panel.style.maxHeight = panel.scrollHeight + 'px';
          var done = function (e) {
            if (e.propertyName !== 'max-height') return;
            panel.style.maxHeight = 'none';
            panel.removeEventListener('transitionend', done);
          };
          panel.addEventListener('transitionend', done);
        } else {
          panel.style.maxHeight = panel.scrollHeight + 'px';
          requestAnimationFrame(function () { requestAnimationFrame(function () { panel.style.maxHeight = '0px'; }); });
        }
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 5) ADD TO CART — a trial funnel wants exactly one thing in the cart, so
   *    clear first, then add, then go straight to checkout. Falls back to a
   *    native form POST if fetch throws.
   * ------------------------------------------------------------------ */
  function bindAtc(scope) {
    collect(scope, '[data-tf-atc]').forEach(function (form) {
      if (!once(form, 'atc')) return;

      form.addEventListener('submit', function (e) {
        var vid = (form.getAttribute('data-variant-id') || '').trim();
        if (!vid) return;                       /* unconfigured: let the guard in markup handle it */
        e.preventDefault();

        var plan = (form.getAttribute('data-selling-plan') || '').trim();
        var btn = form.querySelector('[data-tf-atc-btn]');
        if (btn) { btn.setAttribute('aria-busy', 'true'); btn.classList.add('is-busy'); }

        var item = { id: Number(vid), quantity: 1 };
        if (plan) item.selling_plan = Number(plan);

        var go = function () { window.location.href = '/checkout'; };
        var fail = function () {
          if (btn) { btn.removeAttribute('aria-busy'); btn.classList.remove('is-busy'); }
          form.removeAttribute('data-tf-atc');   /* next click submits natively */
          form.submit();
        };

        fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
          .catch(function () { /* a failed clear must not block the add */ })
          .then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [item] })
            });
          })
          .then(function (r) { if (!r || !r.ok) throw new Error('add failed'); return r.json(); })
          .then(go)
          .catch(fail);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 6) STICKY OFFER BAR — appears once the hero is gone, stands down while the
   *    real offer section is on screen so the two CTAs never compete.
   * ------------------------------------------------------------------ */
  function bindStickyBar(scope) {
    collect(scope, '[data-tf-stickybar]').forEach(function (bar) {
      if (!once(bar, 'stickybar')) return;
      var afterSel = bar.getAttribute('data-tf-after') || '';
      var yieldSel = bar.getAttribute('data-tf-yield') || '';

      addConsumer(bar, function () {
        var after = afterSel ? document.querySelector(afterSel) : null;
        var pastHero = after ? after.getBoundingClientRect().bottom < 40 : window.scrollY > 620;

        var yielding = false;
        if (yieldSel) {
          var y = document.querySelector(yieldSel);
          if (y) {
            var r = y.getBoundingClientRect();
            yielding = r.top < (window.innerHeight * .82) && r.bottom > 90;
          }
        }
        bar.classList.toggle('is-up', pastHero && !yielding);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * SCAN
   * ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
   * 6) MOBILE CHECKOUT RETARGET
   *
   * Brief: "on mobile, all the ctas that do an auto scroll, lets make all of
   * those go straight to checkout rather than scrolling."
   *
   * On desktop the mid-page buttons scroll to the buy box, which is where the
   * offer and the autoship terms are explained. On a phone that scroll costs a
   * whole extra step, so every one of them becomes a direct checkout link.
   *
   * This REWRITES the href rather than intercepting the click, which matters:
   * the browser's own status bar, middle-click, long-press and "open in new tab"
   * all then agree with what tapping does. Intercepting a click leaves the link
   * lying about where it goes.
   *
   * The URL is never written in JS. It arrives on data-tf-mcheckout, rendered
   * from snippets/oh-tf-checkout.liquid, so there is still exactly one source of
   * the variant and selling-plan ids.
   *
   * The original scroll target is stashed on first run so widening the window
   * puts it back. If JS never runs, the href keeps its Liquid value and the
   * button still scrolls to the buy box — degraded, not broken.
   * ------------------------------------------------------------------ */
  var MCHECKOUT_MQ = '(max-width: 900px)';

  function applyMobileCheckout(scope) {
    var mobile = !window.matchMedia || window.matchMedia(MCHECKOUT_MQ).matches;
    collect(scope, '[data-tf-mcheckout]').forEach(function (a) {
      var url = a.getAttribute('data-tf-mcheckout');
      if (!url) return;
      if (!a.hasAttribute('data-tf-scrollhref')) {
        a.setAttribute('data-tf-scrollhref', a.getAttribute('href') || '');
      }
      var want = mobile ? url : a.getAttribute('data-tf-scrollhref');
      if (a.getAttribute('href') !== want) a.setAttribute('href', want);
    });
  }

  var mcBound = false;
  function bindMobileCheckout(scope) {
    applyMobileCheckout(scope);
    if (mcBound) return;
    mcBound = true;
    /* matchMedia change rather than resize: it fires once on the actual
       breakpoint crossing instead of on every pixel of a drag. */
    if (window.matchMedia) {
      var mq = window.matchMedia(MCHECKOUT_MQ);
      var onChange = function () { applyMobileCheckout(document); };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }
  }

  /* ------------------------------------------------------------------
   * 7) PAUSE OFF-SCREEN DECORATIVE ANIMATION
   *
   * Brief: "the scroll is very very jagged ... this page has to be INSANELY
   * smooth." Measured before writing this, over a scripted full-page scroll at
   * 1440px: 124 infinite CSS animations running, 110 of them OFF-SCREEN. Every
   * one is animating transform on its own composited layer, forever, for a gummy
   * nobody can see — and the page is 20,788px tall, so most of them are always
   * off-screen.
   *
   * A/B on the same gesture, animations paused off-screen vs not:
   *     worst frame   100.1ms -> 33.9ms
   *     frames >33ms  7 -> 4
   *     running       124 -> 9
   * Median was already 8.3ms either way; it was never the average frame that felt
   * jagged, it was the long ones, and this cuts the worst by 3x.
   *
   * Also measured and REJECTED: destroying Lenis. Intuition said the rAF-driven
   * smooth scroll was the culprit; the numbers said the opposite — without it,
   * frames over budget went 2.2% -> 9.8% and the worst frame went to 216ms. Lenis
   * stays. Do not "optimise" it away without re-running that A/B.
   *
   * rootMargin keeps a screenful of slack either side so nothing is ever caught
   * mid-pause as it scrolls in. Elements start paused and the observer's first
   * callback un-pauses whatever is actually in view.
   * ------------------------------------------------------------------ */
  var DECOR_ANIM = '.ohtf__bob, .ohtf__spin, .ohtf__wob';

  function bindAnimPause(scope) {
    if (!('IntersectionObserver' in window)) return;
    var els = collect(scope, DECOR_ANIM).filter(function (el) { return once(el, 'animpause'); });
    if (!els.length) return;

    if (!OHTF.__animIO) {
      OHTF.__animIO = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          /* Writing the property only when it actually changes keeps this off the
             style-recalc path during a scroll. */
          var want = e.isIntersecting ? '' : 'paused';
          if (e.target.style.animationPlayState !== want) e.target.style.animationPlayState = want;
        }
      }, { rootMargin: '100% 0px' });
    }
    for (var j = 0; j < els.length; j++) {
      els[j].style.animationPlayState = 'paused';
      OHTF.__animIO.observe(els[j]);
    }
  }

  OHTF.scan = function (root) {
    var scope = root || document;
    bindMag(scope);
    bindPin(scope);
    bindScrub(scope);
    bindCollapse(scope);
    bindAtc(scope);
    bindStickyBar(scope);
    bindMobileCheckout(scope);
    bindAnimPause(scope);
    request();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { OHTF.scan(document); }, { once: true });
  } else {
    OHTF.scan(document);
  }

  document.addEventListener('shopify:section:load', function (e) { OHTF.scan(e.target || document); });
  document.addEventListener('shopify:section:reorder', function () { OHTF.scan(document); });
})();
